import { Divider, IconButton, Stack } from "@mui/material";
import { Category } from "../../../../../domain/model/Category";
import { CustomTextField } from "../../../../component/CustomTextField";
import { useTranslation } from "react-i18next";
import { CustomTextView } from "../../../../component/CustomTextView";
import {
  COLOR_GREEN,
  COLOR_LESS_BLACK,
  COLOR_LIGHT_GRAY,
  COLOR_MAIN,
} from "../../../../../data/colors/Colors";
import { CustomButtonView } from "../../../../component/CustomButtonView";
import { useState } from "react";
import {
  CheckCircleRounded,
  CloseRounded,
  ContentCopyRounded,
} from "@mui/icons-material";
import { UnlockCategoryViewModel } from "./UnlockCategoryViewModel";
import { provideUnlockCategoryUseCase } from "../../../../di/UseCasesModule";
import {
  getReferralLinkFromUnlockCode,
  getUserInfoAgain,
} from "../../../../../core/utils/Utils";
import { copyToClipboard } from "../../../../../core/utils/UtilsInJS";
import { CustomCardView } from "../../../../component/CustomCardView";
import { getReferenceLineUtilityClass } from "@mui/x-charts";

export function UnlockCategoryView({
  category,
  handleClose,
}: {
  category: Category;
  handleClose: () => void;
}) {
  const { t } = useTranslation();
  const viewModel = UnlockCategoryViewModel(provideUnlockCategoryUseCase());

  const [code, setCode] = useState("");
  return (
    <Stack minWidth={"35vw"} p={2} gap={2}>
      <Stack direction={"row"} width={"100%"} justifyContent={"center"} gap={1}>
        <Stack width={"100%"}>
          <CustomTextView
            text={t("unlock_", { categoryName: category.title })}
            variant="h6"
            fontWeight="bold"
          />
          <CustomTextView
            text={t("in_order_to_unlock_this_category_enter_unlock_code")}
            variant="body2"
            textColor={COLOR_LIGHT_GRAY}
          />
        </Stack>

        <IconButton
          onClick={handleClose}
          sx={{
            width: 40,
            height: 40,
            background: COLOR_MAIN,
          }}
        >
          <CloseRounded sx={{ color: "white" }} />
        </IconButton>
      </Stack>

      {viewModel.isUnlocked ? (
        <>
          <Divider />

          <Stack width={"100%"} alignItems={"center"} justifyContent={"center"}>
            <CheckCircleRounded
              sx={{ color: COLOR_GREEN, width: 50, height: 50 }}
            />
            <CustomTextView
              text={t("category_unlocked_successfully")}
              variant="body1"
              textColor={"green"}
            />
          </Stack>
          <Divider />

          <Stack>
            <CustomTextView
              text={t("your_referral_codes")}
              variant="h6"
              fontWeight="bold"
            />
            <CustomTextView
              text={t("share_them_with_friends")}
              variant="body2"
              textColor={COLOR_LIGHT_GRAY}
            />
          </Stack>
          <Stack gap={1} width={"100%"}>
            {viewModel.generatedUnlockCodes.map((unlockCode) => {
              const url = getReferralLinkFromUnlockCode(unlockCode);
              return (
                <CustomCardView
                  padding={2}
                  children={
                    <Stack width={"100%"} sx={{ direction: "ltr" }} gap={2}>
                      <Stack
                        direction={"row"}
                        alignItems={"center"}
                        justifyContent={"space-between"}
                        width={"100%"}
                      >
                        <CustomTextView
                          text={unlockCode.code}
                          fontSize={14}
                          fontWeight="400"
                          props={{
                            wordBreak: "break-all",
                            maxLines: 1,
                            textOverflow: "ellipsis",
                            overflow: "hidden",
                            overflowWrap: "break-word",
                            whiteSpace: "nowrap",
                          }}
                        />
                        <IconButton
                          sx={{ border: "solid 1px black" }}
                          onClick={async () => {
                            try {
                              await copyToClipboard(unlockCode.code);
                              viewModel.showSnackbar("Copied", "success");
                            } catch (e) {
                              alert(e);
                            }
                          }}
                        >
                          <ContentCopyRounded
                            sx={{ color: COLOR_LESS_BLACK }}
                            fontSize="small"
                          />
                        </IconButton>
                      </Stack>

                      <Stack
                        direction={"row"}
                        alignItems={"center"}
                        justifyContent={"space-between"}
                        width={"100%"}
                      >
                        <CustomTextView
                          text={url}
                          fontSize={12}
                          textColor={COLOR_LIGHT_GRAY}
                          fontWeight="400"
                          props={{
                            wordBreak: "break-all",
                            maxLines: 1,
                            textOverflow: "ellipsis",
                            overflow: "hidden",
                            overflowWrap: "break-word",
                            whiteSpace: "nowrap",
                          }}
                        />
                        <IconButton
                          sx={{ border: "solid 1px black" }}
                          onClick={async () => {
                            try {
                              await copyToClipboard(url);
                              viewModel.showSnackbar("Copied", "success");
                            } catch (e) {
                              alert(e);
                            }
                          }}
                        >
                          <ContentCopyRounded
                            sx={{ color: COLOR_LESS_BLACK }}
                            fontSize="small"
                          />
                        </IconButton>
                      </Stack>
                    </Stack>
                  }
                />
              );
            })}
          </Stack>
        </>
      ) : (
        <>
          <CustomTextField
            placeholder={t("unlock_code")}
            onTextChange={(code) => {
              setCode(code);
            }}
            value={code}
          />
          <CustomButtonView
            text={t("unlock")}
            props={{ pt: 0.5, pb: 0.5 }}
            onClick={() => {
              if (!code) return;

              viewModel.unlockCategory({
                code,
                categoryId: category.id,
              });
            }}
          />
        </>
      )}
    </Stack>
  );
}
